import React, { FunctionComponent } from 'react';

import clsx from 'clsx';

import { Product } from '@domains/product';

import { sendClickOnProductLinkEvent } from '@utils/analytics';
import { getFirstCategorySlug } from '@utils/product/route';
import { makeProductRoute } from '@utils/route';
import { applyTypograf } from '@utils/typograf';

import { useProduct } from '@hooks/use-product';
import { useProductAvailability } from '@hooks/use-product-availability';

import { Badge } from '@components/common/Badge/Badge';
import { Button } from '@components/common/Button/Button';
import { FavoriteButton } from '@components/common/FavoriteButton/FavoriteButton';
import { Link } from '@components/common/Link/Link';
import { Picture } from '@components/common/Picture/Picture';
import { QuantityPicker } from '@components/common/QuantityPicker/QuantityPicker';
import { ProductPricePerUnit } from '@components/product/ProductPricePerUnit/ProductPricePerUnit';
import { ProductPricePresenter } from '@components/product/ProductPricePresenter/ProductPricePresenter';

import styles from './ProductCard.module.scss';

type ProductCardProps = {
    product: Product;
    priceDigit?: number;
    isHorizontal?: boolean;
    className?: string;
    onClick?(): void;
};

export const ProductCard: FunctionComponent<ProductCardProps> = (props) => {
    const { product, priceDigit = 0, className, onClick, isHorizontal = false } = props;
    const {
        id,
        name,
        slug,
        primary_image,
        measure_unit,
        purchase_unit,
        is_novelty,
        is_promo,
        is_favorite,
        availability,
        categories,
    } = product;

    const mappedAvailability = useProductAvailability(availability);

    const {
        cartItem,
        isAddToCartPending,
        isDisabledCartInterface,
        handleAddProductToCart,
        handleDebouncedQuantityChange,
        handleRemoveFromCart,
    } = useProduct(id);

    const fistCategorySlug = getFirstCategorySlug(categories);
    const productRoute = fistCategorySlug ? makeProductRoute(slug, fistCategorySlug) : null;

    const imageBlock = (
        <div className={styles.imageContainer}>
            <div className={styles.topControls}>
                <ul className={styles.badges}>
                    {is_novelty && <Badge variant="isNew" className={styles.badge} />}
                    {is_promo && <Badge variant="isPromo" className={styles.badge} />}
                </ul>

                <FavoriteButton
                    type="product"
                    id={id}
                    initialIsFavorite={is_favorite}
                    className={styles.favoriteButton}
                    isDisabled={mappedAvailability.isDisabledFavorite}
                    hasShadow
                />
            </div>

            <Picture
                sources={primary_image}
                classes={{ picture: styles.picture }}
                className={styles.image}
            />
        </div>
    );

    return (
        <article
            className={clsx(styles.root, isHorizontal && styles.isHorizontal, className)}
            data-testid="product-card"
        >
            {productRoute ? (
                <Link
                    route={productRoute}
                    onClick={() => {
                        sendClickOnProductLinkEvent(product);
                        onClick?.();
                    }}
                    className={styles.link}
                    title={`Перейти к товару «${name}»`}
                    data-testid="product-card-link"
                >
                    {imageBlock}
                </Link>
            ) : (
                <div className={styles.link} data-testid="product-card-link">
                    {imageBlock}
                </div>
            )}

            <div className={styles.priceWrapper}>
                <ProductPricePerUnit
                    digit={priceDigit}
                    measureUnit={measure_unit}
                    className={styles.pricePerUnit}
                />
                <ProductPricePresenter
                    purchaseUnit={purchase_unit}
                    measureUnitName={measure_unit.type.name}
                    classes={{
                        root: styles.priceBlock,
                        promotionPrice: styles.promotionPrice,
                        measureUnits: styles.measureUnits,
                    }}
                />
                {productRoute ? (
                    <Link onClick={onClick} route={productRoute} className={styles.name}>
                        {applyTypograf(name)}
                    </Link>
                ) : (
                    <span className={styles.name}>{applyTypograf(name)}</span>
                )}
            </div>
            <div className={styles.buttonWrapper}>
                {cartItem === null || mappedAvailability.isDisabledButton ? (
                    <Button
                        isFullWith
                        variant="greenLight"
                        className={styles.button}
                        disabled={isDisabledCartInterface || mappedAvailability.isDisabledButton}
                        isLoading={isAddToCartPending}
                        onClick={() => handleAddProductToCart(mappedAvailability.isDisabledButton)}
                        data-testid="add-to-cart-btn"
                    >
                        {mappedAvailability.buttonText}
                    </Button>
                ) : (
                    <QuantityPicker
                        initialQuantity={cartItem.quantity}
                        isDisabledInterface={isDisabledCartInterface}
                        onDebounceQuantityChange={handleDebouncedQuantityChange}
                        onRemoveAtZeroQuantity={handleRemoveFromCart}
                        unit={measure_unit.type.name}
                        step={purchase_unit.measure_units}
                        price={purchase_unit.price_in_kopecks.new}
                    />
                )}
            </div>
        </article>
    );
};
